import React, { useState, useEffect, useRef } from 'react';

/**
 * LazyImage Component
 * 
 * Optimized image loading component that:
 * - Lazy loads images using IntersectionObserver
 * - Shows a placeholder while loading
 * - Provides smooth fade-in transition
 * - Supports all standard img attributes
 * - Improves page load performance
 */

const LazyImage = ({
  src,
  alt = '',
  className = '',
  placeholderClassName = '',
  width,
  height,
  priority = false,
  onLoad,
  onError,
  ...props
}) => {
  const [imageSrc, setImageSrc] = useState(priority ? src : null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    // If priority loading, skip intersection observer
    if (priority) {
      return;
    }

    // Set up intersection observer for lazy loading
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !imageSrc) {
            setImageSrc(src);
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before image enters viewport
        threshold: 0.01
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => {
      if (imgRef.current) {
        observer.unobserve(imgRef.current);
      }
    };
  }, [src, imageSrc, priority]);

  const handleLoad = (e) => {
    setImageLoaded(true);
    if (onLoad) {
      onLoad(e);
    }
  };

  const handleError = (e) => {
    setImageError(true);
    if (onError) {
      onError(e);
    }
  };

  return (
    <div
      ref={imgRef}
      className={`relative ${className}`}
      style={{
        width: width || '100%',
        height: height || 'auto'
      }}
    >
      {/* Placeholder - shown while image is loading */}
      {!imageLoaded && !imageError && (
        <div
          className={`absolute inset-0 bg-gray-200 animate-pulse ${placeholderClassName}`}
          aria-hidden="true"
        />
      )}

      {/* Actual Image */}
      {imageSrc && !imageError && (
        <img
          src={imageSrc}
          alt={alt}
          className={`${className} transition-opacity duration-300 ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={handleLoad}
          onError={handleError}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          width={width}
          height={height}
          {...props}
        />
      )}

      {/* Error state */}
      {imageError && (
        <div
          className={`absolute inset-0 bg-gray-100 flex items-center justify-center text-gray-400 text-sm ${placeholderClassName}`}
        >
          <div className="text-center p-4">
            <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span>Image unavailable</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default LazyImage;




